import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';

const CartContext = createContext(null);

const safeGetCart = () => {
  try { return JSON.parse(localStorage.getItem('cart') || '[]'); }
  catch { return []; }
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(safeGetCart);

  // Persist on every change
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = useCallback((product, quantity = 1) => {
    setCartItems(prev => {
      const existing = prev.find(i => i._id === product._id);
      if (existing) {
        return prev.map(i =>
          i._id === product._id
            ? { ...i, quantity: Math.min(i.quantity + quantity, product.stock) }
            : i
        );
      }
      // Store minimal product data — avoid bloating localStorage
      return [...prev, {
        _id: product._id, name: product.name, price: product.price,
        image: product.images?.[0], brand: product.brand,
        stock: product.stock, quantity
      }];
    });
  }, []);

  const removeFromCart = useCallback((id) =>
    setCartItems(prev => prev.filter(i => i._id !== id)), []);

  const updateQuantity = useCallback((id, quantity) => {
    if (quantity <= 0) return removeFromCart(id);
    setCartItems(prev => prev.map(i => {
      if (i._id !== id) return i;
      const maxQty = i.stock || 999;
      if (quantity > maxQty) {
        toast.error(`Only ${maxQty} units available in stock`);
        return { ...i, quantity: maxQty };
      }
      return { ...i, quantity };
    }));
  }, [removeFromCart]);

  const clearCart = useCallback(() => setCartItems([]), []);

  // Memoised derived values — avoid recalculating on every render
  const { cartTotal, cartCount } = useMemo(() => ({
    cartTotal: cartItems.reduce((s, i) => s + i.price * i.quantity, 0),
    cartCount: cartItems.reduce((s, i) => s + i.quantity, 0),
  }), [cartItems]);

  return (
    <CartContext.Provider value={{ cartItems, addToCart, removeFromCart, updateQuantity, clearCart, cartTotal, cartCount }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};
